import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiPlus, FiTrash2, FiUsers } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { jobOpeningsAPI, settingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Common/Modal';

const JOB_TYPES = ['Internship', 'Alternance', 'Full-time', 'Part-time'];
const CONTRACT_TYPES = ['CDI', 'CDD', 'Alternance', 'Stage', 'Freelance', 'Other'];
const PLACEMENT_STAGE_OPTIONS = ['job_dating', 'ongoing_placement', 'active_search', 'priority_placement'];

const JobDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { canPerformAction } = useAuth();
  const canUpdateJobs = canPerformAction('jobs', 'update');
  const canDeleteJobs = canPerformAction('jobs', 'delete');

  const [job, setJob] = useState(null);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [jobStudentStatuses, setJobStudentStatuses] = useState([]);
  const [selectableStudents, setSelectableStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAssignedStudents, setLoadingAssignedStudents] = useState(false);
  const [loadingSelectableStudents, setLoadingSelectableStudents] = useState(false);
  const [savingStudents, setSavingStudents] = useState(false);
  const [showAddStudentsModal, setShowAddStudentsModal] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [programTypes, setProgramTypes] = useState([]);
  const [form, setForm] = useState({
    offer_title: '',
    program_type: 'All',
    job_type: '',
    contract_type: '',
    placement_stage: '',
    number_of_positions: 1,
    responsibilities: '',
    exchange_history: '',
    last_contact_date: '',
    next_followup_date: '',
    followup_action: '',
    email_stage: '',
    reminder_enabled: false,
    assigned_manager_id: '',
    handover_manager_id: '',
    meeting_history: ''
  });
  const defaultJobStudentStatus = useMemo(() => {
    const firstActive = (jobStudentStatuses || []).find((s) => String(s?.status || '').toLowerCase() === 'active');
    return firstActive?.status_name || 'Applied';
  }, [jobStudentStatuses]);

  const jobTypeLabel = (v) => {
    const key = {
      'Full-time': 'jobs.types.fullTime',
      'Part-time': 'jobs.types.partTime',
      Internship: 'jobs.types.internship',
      Alternance: 'jobs.types.alternance'
    }[v];
    return key ? t(key) : v;
  };

  const placementStageLabel = (s) => {
    const key = {
      job_dating: 'companies.relationshipStages.jobDating',
      ongoing_placement: 'companies.relationshipStages.ongoingPlacement',
      active_search: 'companies.relationshipStages.activeSearch',
      priority_placement: 'companies.relationshipStages.priorityPlacement'
    }[s];
    return key ? t(key) : (s || '-');
  };

  const loadJob = useCallback(async () => {
    setLoading(true);
    try {
      const res = await jobOpeningsAPI.getById(id);
      const data = res?.data || null;
      setJob(data);
      if (data) {
        setForm({
          offer_title: data.offer_title || data.job_title || '',
          program_type: data.program_type || 'All',
          job_type: data.job_type || '',
          contract_type: data.contract_type || '',
          placement_stage: data.placement_stage || '',
          number_of_positions: data.number_of_positions ?? 1,
          responsibilities: data.responsibilities || '',
          exchange_history: data.exchange_history || '',
          last_contact_date: data.last_contact_date ? String(data.last_contact_date).slice(0, 10) : '',
          next_followup_date: data.next_followup_date ? String(data.next_followup_date).slice(0, 10) : '',
          followup_action: data.followup_action || '',
          email_stage: data.email_stage || '',
          reminder_enabled: Number(data.reminder_enabled) === 1,
          assigned_manager_id: data.assigned_manager_id || '',
          handover_manager_id: data.handover_manager_id || '',
          meeting_history: data.meeting_history || ''
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || t('jobs.failedLoad'));
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  const loadAssignedStudents = useCallback(async () => {
    setLoadingAssignedStudents(true);
    try {
      const res = await jobOpeningsAPI.getStudents(id);
      setAssignedStudents(res?.data?.students || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    } finally {
      setLoadingAssignedStudents(false);
    }
  }, [id, t]);

  const loadJobStudentStatuses = useCallback(async () => {
    try {
      const res = await settingsAPI.getJobStudentStatuses();
      setJobStudentStatuses(res?.data || []);
    } catch (_) {
      setJobStudentStatuses([]);
    }
  }, []);

  useEffect(() => {
    settingsAPI
      .getProgramTypes()
      .then((r) => {
        const rows = Array.isArray(r?.data) ? r.data : [];
        const active = rows.filter((p) => String(p?.status || 'active').toLowerCase() === 'active');
        setProgramTypes(active);
      })
      .catch(() => setProgramTypes([]));
  }, []);

  const loadSelectableStudents = useCallback(async (searchValue = '') => {
    setLoadingSelectableStudents(true);
    try {
      const res = await jobOpeningsAPI.getSelectableStudents(id, { search: searchValue, limit: 300 });
      setSelectableStudents(res?.data?.students || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    } finally {
      setLoadingSelectableStudents(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  useEffect(() => {
    loadAssignedStudents();
    loadJobStudentStatuses();
  }, [loadAssignedStudents, loadJobStudentStatuses]);

  useEffect(() => {
    if (!showAddStudentsModal) return;
    loadSelectableStudents(studentSearch);
  }, [showAddStudentsModal, studentSearch, loadSelectableStudents]);

  const handleUpdate = async () => {
    if (!form.offer_title.trim()) {
      toast.error(t('jobs.titleRequired'));
      return;
    }
    try {
      await jobOpeningsAPI.update(id, {
        ...form,
        number_of_positions: Math.max(1, Number(form.number_of_positions) || 1),
        last_contact_date: form.last_contact_date || null,
        next_followup_date: form.next_followup_date || null
      });
      toast.success(t('jobs.updated'));
      setShowEditModal(false);
      loadJob();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('jobs.deleteConfirm'))) return;
    try {
      await jobOpeningsAPI.delete(id);
      toast.success(t('jobs.deleted'));
      navigate('/jobs');
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  const openAddStudentsModal = () => {
    setSelectedStudentIds([]);
    setStudentSearch('');
    setShowAddStudentsModal(true);
  };

  const toggleStudentSelection = (studentId) => {
    const sid = Number(studentId);
    setSelectedStudentIds((prev) => {
      if (prev.includes(sid)) return prev.filter((x) => x !== sid);
      return [...prev, sid];
    });
  };

  const toggleSelectAllVisibleStudents = () => {
    const visibleIds = selectableStudents.map((s) => Number(s.student_id)).filter(Boolean);
    setSelectedStudentIds((prev) => {
      const prevSet = new Set(prev);
      const allSelected = visibleIds.length > 0 && visibleIds.every((sid) => prevSet.has(sid));
      if (allSelected) {
        return prev.filter((sid) => !visibleIds.includes(sid));
      }
      const next = new Set(prev);
      visibleIds.forEach((sid) => next.add(sid));
      return [...next];
    });
  };

  const handleAssignStudents = async () => {
    if (!selectedStudentIds.length) {
      toast.error('Please select at least one student');
      return;
    }
    try {
      setSavingStudents(true);
      await jobOpeningsAPI.assignStudents(id, {
        students: selectedStudentIds.map((studentId) => ({
          student_id: studentId,
          status: defaultJobStudentStatus
        }))
      });
      toast.success('Students assigned successfully');
      setShowAddStudentsModal(false);
      setSelectedStudentIds([]);
      loadAssignedStudents();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    } finally {
      setSavingStudents(false);
    }
  };

  const handleUpdateAssignedStudentStatus = async (linkId, status) => {
    try {
      await jobOpeningsAPI.updateAssignedStudent(id, linkId, { status });
      setAssignedStudents((prev) => prev.map((s) => (s.id === linkId ? { ...s, status } : s)));
      toast.success('Status updated');
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  const handleRemoveAssignedStudent = async (linkId) => {
    if (!window.confirm('Remove this student from this job opening?')) return;
    try {
      await jobOpeningsAPI.removeAssignedStudent(id, linkId);
      setAssignedStudents((prev) => prev.filter((s) => s.id !== linkId));
      toast.success('Student removed');
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  if (loading) {
    return <div className="loading-spinner"><div className="spinner" /></div>;
  }
  if (!job) {
    return null;
  }

  return (
    <div>
      <div className="detail-header">
        <div className="detail-left">
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/jobs')}>
            <FiArrowLeft /> {t('common.back')}
          </button>
          <h2 className="detail-title">{job.offer_title || job.job_title}</h2>
          <div className="detail-meta">
            <span>{t('jobs.company')}: {job.company_name || '-'}</span>
            <span>{t('settings.fields.programType', 'Program Type')}: {job.program_type || 'All'}</span>
          </div>
        </div>
        <div className="detail-right" style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
          {canUpdateJobs && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowEditModal(true)}>
              <FiEdit /> {t('common.edit')}
            </button>
          )}
          {canDeleteJobs && (
            <button className="btn btn-danger btn-sm" onClick={handleDelete}>
              <FiTrash2 /> {t('common.delete')}
            </button>
          )}
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 14 }}>
        <button
          type="button"
          className={activeTab === 'details' ? 'active' : ''}
          onClick={() => setActiveTab('details')}
        >
          {t('common.details', 'Details')}
        </button>
        <button
          type="button"
          className={activeTab === 'students' ? 'active' : ''}
          onClick={() => setActiveTab('students')}
        >
          Students
        </button>
      </div>

      {activeTab === 'details' && (
        <div className="card" style={{ padding: 18 }}>
          <h3 style={{ marginTop: 0 }}>{t('common.details', 'Details')}</h3>
          <div className="detail-info-grid">
            <div className="detail-info-item"><label>{t('jobs.offerTitle')}</label><span>{job.offer_title || job.job_title || '-'}</span></div>
            <div className="detail-info-item"><label>{t('jobs.company')}</label><span>{job.company_name || '-'}</span></div>
            <div className="detail-info-item"><label>{t('settings.fields.programType', 'Program Type')}</label><span>{job.program_type || 'All'}</span></div>
            <div className="detail-info-item"><label>{t('jobs.jobType')}</label><span>{job.job_type || '-'}</span></div>
            <div className="detail-info-item"><label>{t('jobs.contractType')}</label><span>{job.contract_type || '-'}</span></div>
            <div className="detail-info-item"><label>{t('companies.relationshipStage', 'Placement Stage')}</label><span>{placementStageLabel(job.placement_stage) || '-'}</span></div>
            <div className="detail-info-item"><label>{t('jobs.positions', 'Number of Positions')}</label><span>{Number(job.number_of_positions || 1)}</span></div>
            <div className="detail-info-item"><label>{t('jobs.assignedManager')}</label><span>{job.assigned_manager_name || '-'}</span></div>
            <div className="detail-info-item"><label>{t('jobs.handoverManager')}</label><span>{job.handover_manager_name || '-'}</span></div>
            <div className="detail-info-item"><label>{t('jobs.lastContact')}</label><span>{job.last_contact_date || '-'}</span></div>
            <div className="detail-info-item"><label>{t('jobs.nextFollowup')}</label><span>{job.next_followup_date || '-'}</span></div>
            <div className="detail-info-item"><label>{t('jobs.reminderEnabled')}</label><span>{Number(job.reminder_enabled) === 1 ? t('common.yes') : t('common.no')}</span></div>
            <div className="detail-info-item full-width"><label>{t('jobs.responsibilities')}</label><span>{job.responsibilities || '-'}</span></div>
            <div className="detail-info-item full-width"><label>{t('jobs.exchangeHistory')}</label><span>{job.exchange_history || '-'}</span></div>
            <div className="detail-info-item full-width"><label>{t('jobs.followupAction')}</label><span>{job.followup_action || '-'}</span></div>
            <div className="detail-info-item"><label>{t('jobs.emailStage')}</label><span>{job.email_stage || '-'}</span></div>
            <div className="detail-info-item full-width"><label>{t('jobs.meetingHistory')}</label><span>{job.meeting_history || '-'}</span></div>
          </div>
        </div>
      )}

      {activeTab === 'students' && (
      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <FiUsers /> Students
          </h3>
          {canUpdateJobs && (
            <button className="btn btn-primary btn-sm" onClick={openAddStudentsModal}>
              <FiPlus /> Add Students
            </button>
          )}
        </div>
        {loadingAssignedStudents ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Student Ref</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Program Type</th>
                  <th>Status</th>
                  <th>Assigned At</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {assignedStudents.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.student_id ? (
                        <button
                          type="button"
                          className="btn-link"
                          style={{ padding: 0, border: 0, background: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => navigate(`/students/${s.student_id}`)}
                        >
                          {s.student_ref || '-'}
                        </button>
                      ) : (s.student_ref || '-')}
                    </td>
                    <td>
                      {s.student_id ? (
                        <button
                          type="button"
                          className="btn-link"
                          style={{ padding: 0, border: 0, background: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => navigate(`/students/${s.student_id}`)}
                        >
                          {[s.first_name, s.last_name].filter(Boolean).join(' ') || '-'}
                        </button>
                      ) : ([s.first_name, s.last_name].filter(Boolean).join(' ') || '-')}
                    </td>
                    <td>{s.email || '-'}</td>
                    <td>{s.phone || '-'}</td>
                    <td>{s.program_type || '-'}</td>
                    <td>
                      <select
                        value={s.status || defaultJobStudentStatus}
                        onChange={(e) => handleUpdateAssignedStudentStatus(s.id, e.target.value)}
                        disabled={!canUpdateJobs}
                      >
                        {(jobStudentStatuses.length ? jobStudentStatuses.filter((st) => String(st?.status || '').toLowerCase() === 'active') : [{ status_name: 'Applied' }])
                          .map((st) => (
                            <option key={st.status_id || st.status_name} value={st.status_name}>{st.status_name}</option>
                          ))}
                      </select>
                    </td>
                    <td>{s.assigned_at ? new Date(s.assigned_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                    <td>
                      {canUpdateJobs && (
                        <button className="delete" title={t('common.remove', 'Remove')} onClick={() => handleRemoveAssignedStudent(s.id)}>
                          <FiTrash2 />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {assignedStudents.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center" style={{ padding: 20, color: 'var(--text-muted)' }}>
                      No students assigned yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={t('jobs.editTitle')}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setShowEditModal(false)}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={handleUpdate}>{t('common.update')}</button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('jobs.offerTitle')} <span className="required">*</span></label>
            <input value={form.offer_title} onChange={(e) => setForm((f) => ({ ...f, offer_title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('jobs.positions', 'Number of Positions')}</label>
            <input
              type="number"
              min="1"
              value={form.number_of_positions}
              onChange={(e) => setForm((f) => ({ ...f, number_of_positions: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label>{t('settings.fields.programType', 'Program Type')}</label>
            <select value={form.program_type} onChange={(e) => setForm((f) => ({ ...f, program_type: e.target.value }))}>
              <option value="All">{t('common.all', 'All')}</option>
              {programTypes.map((p) => (
                <option key={p.program_type_id || p.program_type_name} value={p.program_type_name}>
                  {p.program_type_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.jobType')}</label>
            <select value={form.job_type} onChange={(e) => setForm((f) => ({ ...f, job_type: e.target.value }))}>
              <option value="">{t('common.select')}</option>
              {JOB_TYPES.map((jt) => (
                <option key={jt} value={jt}>{jobTypeLabel(jt)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.contractType')}</label>
            <select value={form.contract_type} onChange={(e) => setForm((f) => ({ ...f, contract_type: e.target.value }))}>
              <option value="">{t('common.select')}</option>
              {CONTRACT_TYPES.map((ct) => <option key={ct} value={ct}>{t(`jobs.contractType.${ct}`, { defaultValue: ct })}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('companies.relationshipStage', 'Placement Stage')}</label>
            <select value={form.placement_stage} onChange={(e) => setForm((f) => ({ ...f, placement_stage: e.target.value }))}>
              <option value="">{t('common.select')}</option>
              {PLACEMENT_STAGE_OPTIONS.map((s) => (
                <option key={s} value={s}>{placementStageLabel(s)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.lastContact')}</label>
            <input type="date" value={form.last_contact_date} onChange={(e) => setForm((f) => ({ ...f, last_contact_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('jobs.nextFollowup')}</label>
            <input type="date" value={form.next_followup_date} onChange={(e) => setForm((f) => ({ ...f, next_followup_date: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.responsibilities')}</label>
            <textarea rows={3} value={form.responsibilities} onChange={(e) => setForm((f) => ({ ...f, responsibilities: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.exchangeHistory')}</label>
            <textarea rows={3} value={form.exchange_history} onChange={(e) => setForm((f) => ({ ...f, exchange_history: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.followupAction')}</label>
            <textarea rows={2} value={form.followup_action} onChange={(e) => setForm((f) => ({ ...f, followup_action: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('jobs.emailStage')}</label>
            <input value={form.email_stage} onChange={(e) => setForm((f) => ({ ...f, email_stage: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('jobs.reminderEnabled')}</label>
            <select value={form.reminder_enabled ? '1' : '0'} onChange={(e) => setForm((f) => ({ ...f, reminder_enabled: e.target.value === '1' }))}>
              <option value="0">{t('common.no')}</option>
              <option value="1">{t('common.yes')}</option>
            </select>
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.meetingHistory')}</label>
            <textarea rows={2} value={form.meeting_history} onChange={(e) => setForm((f) => ({ ...f, meeting_history: e.target.value }))} />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showAddStudentsModal}
        onClose={() => setShowAddStudentsModal(false)}
        title="Add Students"
        size="lg"
        footer={(
          <>
            <button className="btn btn-outline" disabled={savingStudents} onClick={() => setShowAddStudentsModal(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={savingStudents || !selectedStudentIds.length} onClick={handleAssignStudents}>
              {savingStudents ? t('common.saving', 'Saving...') : `Add Selected (${selectedStudentIds.length})`}
            </button>
          </>
        )}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 8,
              alignItems: 'center',
              padding: 0,
              borderRadius: 10,
              border: 'none',
              background: 'transparent'
            }}
          >
            <div className="search-box" style={{ marginBottom: 0 }}>
              <input
                placeholder="Search students by name, ref, email, phone"
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={toggleSelectAllVisibleStudents}
                disabled={!selectableStudents.length || loadingSelectableStudents}
              >
                {selectableStudents.length > 0 && selectableStudents.every((s) => selectedStudentIds.includes(Number(s.student_id)))
                  ? 'Unselect All'
                  : 'Select All'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={() => setSelectedStudentIds([])}
                disabled={!selectedStudentIds.length}
              >
                Clear
              </button>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
              fontSize: 12,
              marginBottom: 2,
              color: 'var(--text-muted)'
            }}
          >
            <span
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                background: 'var(--primary-soft, rgba(59,130,246,0.12))',
                color: 'var(--primary)',
                fontWeight: 700
              }}
            >
              {selectedStudentIds.length} selected
            </span>
            <span>
              {job?.program_type && String(job.program_type).toLowerCase() !== 'all'
                ? `Program filter: ${job.program_type}`
                : 'Program filter: All students'}
            </span>
          </div>

          {loadingSelectableStudents ? (
            <div className="loading-spinner"><div className="spinner" /></div>
          ) : (
            <div
              className="table-responsive"
              style={{
                maxHeight: 380,
                overflowY: 'auto',
                border: '1px solid var(--border)',
                borderRadius: 10,
                marginTop: 2
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>Student Ref</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Program Type</th>
                  </tr>
                </thead>
                <tbody>
                  {selectableStudents.map((s) => (
                    <tr key={s.student_id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedStudentIds.includes(Number(s.student_id))}
                          onChange={() => toggleStudentSelection(s.student_id)}
                        />
                      </td>
                      <td>{s.student_ref || '-'}</td>
                      <td style={{ fontWeight: 600 }}>{[s.first_name, s.last_name].filter(Boolean).join(' ') || '-'}</td>
                      <td>{s.email || '-'}</td>
                      <td>{s.phone || '-'}</td>
                      <td>{s.program_type || '-'}</td>
                    </tr>
                  ))}
                  {selectableStudents.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center" style={{ padding: 20, color: 'var(--text-muted)' }}>
                        No students available for this job/program
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default JobDetail;
